export type MerryChatActivityStatus =
  | "complete"
  | "dot"
  | "progress"
  | "question";

export interface MerryChatActivityItem {
  id: string;
  preview: string;
  status: MerryChatActivityStatus;
  time?: string;
  title: string;
}

export type MerryChatNavigationIndicator =
  | { type: "none" }
  | { count: number; type: "count" }
  | { type: MerryChatActivityStatus };

export interface MerryChatNotificationVisualState {
  isExpanded: boolean;
  isManagementVisible: boolean;
  items: ReadonlyArray<MerryChatActivityItem>;
}

export type MerryChatHistoryDrawerSize = "anchored" | "viewport";

export function deriveMerryChatNavigationIndicator(
  items: ReadonlyArray<MerryChatActivityItem>,
): MerryChatNavigationIndicator {
  if (items.some((item) => item.status === "question")) {
    return { type: "question" };
  }

  if (items.some((item) => item.status === "progress")) {
    return { type: "progress" };
  }

  const unreadCount = items.filter((item) => item.status === "dot").length;
  if (unreadCount === 1) return { type: "dot" };
  if (unreadCount > 1) return { count: unreadCount, type: "count" };

  if (items.some((item) => item.status === "complete")) {
    return { type: "complete" };
  }

  return { type: "none" };
}

export function markMerryChatItemsRead(
  items: ReadonlyArray<MerryChatActivityItem>,
): ReadonlyArray<MerryChatActivityItem> {
  return items.map((item) =>
    item.status === "dot" ? { ...item, status: "complete" } : item,
  );
}

export function getMerryChatHistoryDrawerSize(
  itemCount: number,
  largeContentThreshold = 7,
): MerryChatHistoryDrawerSize {
  return itemCount > largeContentThreshold ? "viewport" : "anchored";
}

export function getMerryChatNotificationFigmaNodeId({
  isExpanded,
  isManagementVisible,
  items,
}: MerryChatNotificationVisualState) {
  if (items.length === 0) return null;
  if (isExpanded && items.length > 1) return "567:119588";
  if (items.length > 1) {
    return isManagementVisible ? "567:118742" : "567:125430";
  }

  switch (items[0]?.status) {
    case "complete":
      return "567:121855";
    case "progress":
      return "567:121136";
    case "question":
      return "567:120420";
    case "dot":
    default:
      return "567:117224";
  }
}

export function getMerryChatIndicatorAccessibleLabel(
  indicator: MerryChatNavigationIndicator,
) {
  switch (indicator.type) {
    case "complete":
      return "有任务已完成";
    case "count":
      return `有 ${indicator.count} 条未读消息`;
    case "dot":
      return "有未读消息";
    case "progress":
      return "有任务进行中";
    case "question":
      return "有任务需要解答";
    case "none":
      return null;
  }
}
